/* GridRowRenderer.js */

"use strict";

const _ = require('lodash');
const GridCell = require('../cell/GridCellComponent');

class GridRowRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("grid-row");
        this._rootElem.component = this._model;
        this._rootElem.tabIndex = -1;

        this._cells = [];

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        let shutdownPromiseArray = [];
        this._unregisterListeners();
        this._model = undefined;
        this._rootElem.remove();
        this._textField = undefined;
        this._rootElem = undefined;

        this._cells.forEach((cell) => {
            cell.removeAllListeners('CellAction');
            shutdownPromiseArray.push(cell.shutdown());
        });
        this._cells = undefined;
        return Promise.all(shutdownPromiseArray);
    }
    _registerListeners(){
        this._model.addListener("ID",this._onId = this._onId.bind(this));
        this._model.addListener("Entity",this._onEntity = this._onEntity.bind(this));
        this._model.addListener("UpdateEntityProperties",this._onUpdateEntityProperties = this._onUpdateEntityProperties.bind(this));
        this._model.addListener("ColumnOrder",this._onColumnOrder = this._onColumnOrder.bind(this));
        this._model.addListener("ColumnComponents",this._onColumnComponents = this._onColumnComponents.bind(this));
        this._model.addListener("Selected",this._onSelected = this._onSelected.bind(this));
        this._model.addListener("NestedLevel",this._onNestedLevel = this._onNestedLevel.bind(this));
        this._model.addListener("ParentId",this._onParentId = this._onParentId.bind(this));
        this._rootElem.addEventListener('click', this._onRowClick = this._onRowClick.bind(this));
        this._rootElem.addEventListener('dblclick', this._onRowDoubleClick = this._onRowDoubleClick.bind(this));
        this._rootElem.addEventListener('mouseenter', this._onMouseEnter = this._onMouseEnter.bind(this));
        this._rootElem.addEventListener('mouseleave', this._onMouseLeave = this._onMouseLeave.bind(this));
    }
    _unregisterListeners(){
        this._model.removeListener("ID",this._onId);
        this._model.removeListener("Entity",this._onEntity);
        this._model.removeListener("UpdateEntityProperties",this._onUpdateEntityProperties);
        this._model.removeListener("ColumnOrder",this._onColumnOrder);
        this._model.removeListener("ColumnComponents",this._onColumnComponents);
        this._model.removeListener("Selected",this._onSelected);
        this._model.removeListener("NestedLevel",this._onNestedLevel);
        this._model.removeListener("ParentId",this._onParentId);
        this._rootElem.removeEventListener('click', this._onRowClick);
        this._rootElem.removeEventListener('dblclick', this._onRowDoubleClick);
        this._rootElem.removeEventListener('mouseenter', this._onMouseEnter);
        this._rootElem.removeEventListener('mouseleave', this._onMouseLeave);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._setId(this._model.getId());
            this._setEntity(this._model.getEntity());
            this._setNestedLevel(this._model.getNestedLevel());
            this._setSelected(this._model.getSelected());
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the unique key for the row has changed
     * @param newVal {string}
     * @private
     */
    _onId(newVal){
        this._setId(newVal);
    }

    /**
     * Fired when the entity has changed
     * @param newVal {object}
     * @private
     */
    _onEntity(newVal){
        this._setEntity(newVal);
    }

    /**
     * Fired when the entity has been updated
     * @param newProperties {object}
     * @private
     */
    _onUpdateEntityProperties(newProperties){
        this._updateEntityProperties(newProperties);
    }

    /**
     * Fired when the grid column order has changed
     * @param newVal {array}
     * @private
     */
    _onColumnOrder(newVal){
        this._setColumnOrder(newVal);
    }

    /**
     * Fired when the grid columns have changed
     * @param newVal {object}
     * @private
     */
    _onColumnComponents(newVal){
        this._setColumns(newVal);
    }

    /**
     * Fired when a row has been selected
     * @param newVal {boolean}
     * @private
     */
    _onSelected(newVal){
        this._setSelected(newVal);
    }

    /**
     * Fired when the nested level has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onNestedLevel(newVal){
        this._setNestedLevel(newVal);
    }

    /**
     * Fired when the parent id has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onParentId(newVal){
        this._setParentId(newVal);
    }

    /**
     * Fired when a row has been selected by the user
     * @param event {event}
     * @private
     */
    _onRowClick(event){
        this._model._triggerRowUserSelected(event);
    }

    /**
     * Fired when a row has been selected by the user
     * @param event {event}
     * @private
     */
    _onRowDoubleClick(event){
        this._model._triggerRowUserDoubleClicked(event);
    }

    /**
     * Fired when the user has entered the row with the mouse
     * @param event {event}
     * @private
     */
    _onMouseEnter(/*event*/){
        this._rootElem.classList.add('active');
    }

    /**
     * Fired when the user has left the row with the mouse
     * @param event {event}
     * @private
     */
    _onMouseLeave(/*event*/){
        this._rootElem.classList.remove('active');
    }

    /**
     * Catch any actions emitted from the inner cells
     * @param eventData {object}
     * @private
     */
    _onCellAction(eventData){
        this._model._triggerRowAction(eventData);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the row id in the DOM somewhere?
     * @param newVal {string}
     * @private
     */
    _setId(newVal){
        if (!_.isUndefined(newVal)) {
            this._rootElem.dataset.id = newVal;
        } else {
            this._rootElem.removeAttribute('data-id');
        }

    }

    /**
     * Build the row display
     * @param newVal {object}
     * @private
     */
    _setEntity(entity){
        this._cells.forEach((cell) => {
            cell.removeAllListeners('CellAction');
            cell.shutdown();
        });
        this._cells = [];
        this._rootElem.innerHTML = '';
        let columnOrder = this._model.getColumnOrder(),
            columnComponents = this._model.getColumnComponents();
        columnOrder.forEach((columnKey) => {
            let cell = new GridCell({
                id: columnKey,
                columnRendererComponent: columnComponents[columnKey].getColumnRendererComponent(),
                entityColumn: columnComponents[columnKey].getEntityColumn(),
                entity: entity,
                value: entity.getProperty(columnKey),
                width: columnComponents[columnKey].getWidth()
            });
            this._cells.push(cell);
            cell.addListener('CellAction', this._onCellAction = this._onCellAction.bind(this));
            this._rootElem.appendChild(cell.renderer.rootElement);
        });
    }

    /**
     * Update any properties at the cell level that have changed
     * @param newProperties {object}
     * @private
     */
    _updateEntityProperties(newProperties){
        this._cells.forEach((cellComponent) => {
            let cellId = cellComponent.getId(),
                oldValue = cellComponent.getValue(),
                newValue = newProperties[cellId];
            if (!_.isUndefined(newValue) && oldValue !== newValue) {
                cellComponent.setValue(newValue);
            }
        });
    }

    /**
     * Rebuild the row with the new column order
     * @param newVal {array}
     * @private
     */
    _setColumnOrder(/*newVal*/){
        this._setEntity(this._model.getEntity());
    }

    /**
     * Rebuild the row with the new columns
     * @param newVal {array}
     * @private
     */
    _setColumns(/*newVal*/){
        this._setEntity(this._model.getEntity());
    }

    /**
     * Select/Deselect the row
     * @param newVal {boolean}
     * @private
     */
    _setSelected(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('selected');
        } else {
            this._rootElem.classList.remove('selected');
        }
    }

    /**
     * Adjusted the nested display
     * @param newVal {number}
     * @private
     */
    _setNestedLevel(newVal){
        this._rootElem.classList.add('nested_' + newVal);
    }

    /**
     * Do something with the parent id?
     * @param newVal {string}
     * @private
     */
    _setParentId(/*newVal*/){
        // Here if needed
    }


    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = GridRowRenderer;